import React, { useState } from "react";
import Modal from "../Modal";
import api from "../../lib/api";
import { ApexDatePicker, toIsoDate } from "../ApexDatePicker";
import { CalendarPlus } from "lucide-react";

const SERVICE_TYPES = [
  { id: "exterior", label: "Exteriér" },
  { id: "interior", label: "Interiér" },
  { id: "complete", label: "Komplet" },
];
const TIERS = [
  { id: "classic", label: "Classic" },
  { id: "detailing", label: "Premium" },
];
const VEHICLE_TYPES = [
  { id: "osobni", label: "Osobní vůz" },
  { id: "suv_mpv", label: "SUV / MPV" },
  { id: "van", label: "VAN / dodávka" },
];

const EMPTY = {
  email: "", firstName: "", lastName: "", phone: "",
  serviceType: "exterior", serviceTier: "classic",
  vehicleType: "osobni", vehicleBrandModel: "",
  date: "", time: "", locationType: "studio",
  serviceAddress: "", addressNote: "",
  paymentMethod: "cash", notes: "",
};

/**
 * AdminCreateBookingModal — admin creates a booking on behalf of a customer
 * who called by phone. The term is already agreed, so this inserts the
 * booking directly as "confirmed" with the real date/time.
 */
export default function AdminCreateBookingModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError("");
    if (!form.email.trim() || !form.firstName.trim() || !form.lastName.trim() || !form.phone.trim()) {
      setError("Vyplňte údaje o zákazníkovi.");
      return;
    }
    if (!form.vehicleBrandModel.trim()) {
      setError("Vyplňte značku a model vozu.");
      return;
    }
    if (!form.date || !form.time) {
      setError("Vyplňte datum i čas domluveného termínu.");
      return;
    }
    if (form.locationType === "mobile" && !form.serviceAddress.trim()) {
      setError("Pro mobilní službu vyplňte adresu.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/employee/bookings/manual-create", form);
      setForm(EMPTY);
      onCreated?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Rezervaci se nepodařilo vytvořit.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Nová rezervace za zákazníka" testId="admin-create-booking-modal">
      <div className="space-y-5">
        {error && <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-3 py-2" data-testid="admin-create-booking-error">{error}</div>}

        <div>
          <div className="text-xs text-apex-cream/60 font-semibold uppercase tracking-wider mb-2">Zákazník</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="E-mail" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-email" />
            <input value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="Telefon" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-phone" />
            <input value={form.firstName} onChange={(e) => set("firstName")(e.target.value)} placeholder="Jméno" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-firstname" />
            <input value={form.lastName} onChange={(e) => set("lastName")(e.target.value)} placeholder="Příjmení" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-lastname" />
          </div>
        </div>

        <div>
          <div className="text-xs text-apex-cream/60 font-semibold uppercase tracking-wider mb-2">Služba</div>
          <div className="flex gap-2 mb-3">
            {TIERS.map((t) => (
              <button key={t.id} onClick={() => set("serviceTier")(t.id)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${form.serviceTier === t.id ? "bg-apex-gold text-apex-bg" : "bg-apex-bg border border-apex-line text-apex-cream/75"}`} data-testid={`admin-create-booking-tier-${t.id}`}>{t.label}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-2 mb-3">
            {SERVICE_TYPES.map((s) => (
              <button key={s.id} onClick={() => set("serviceType")(s.id)} className={`px-3 py-2 rounded-lg text-sm transition ${form.serviceType === s.id ? "bg-apex-gold text-apex-bg font-semibold" : "bg-apex-bg border border-apex-line text-apex-cream/75"}`} data-testid={`admin-create-booking-svc-${s.id}`}>{s.label}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <select value={form.vehicleType} onChange={(e) => set("vehicleType")(e.target.value)} className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-vehicletype">
              {VEHICLE_TYPES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
            <input value={form.vehicleBrandModel} onChange={(e) => set("vehicleBrandModel")(e.target.value)} placeholder="Značka a model (např. Škoda Octavia)" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-vehicle" />
          </div>
        </div>

        <div>
          <div className="text-xs text-apex-cream/60 font-semibold uppercase tracking-wider mb-2">Domluvený termín</div>
          <div className="flex flex-wrap gap-3 items-end">
            <label className="text-xs text-apex-cream/60">
              <span className="block mb-1">Datum</span>
              <ApexDatePicker value={form.date ? new Date(`${form.date}T00:00:00`) : undefined} onSelect={(d) => set("date")(toIsoDate(d))} placeholder="Vyberte datum" testId="admin-create-booking-date" />
            </label>
            <label className="text-xs text-apex-cream/60">
              <span className="block mb-1">Čas</span>
              <input type="time" value={form.time} onChange={(e) => set("time")(e.target.value)} className="px-3 py-2 rounded-lg bg-apex-card border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-time" />
            </label>
          </div>
        </div>

        <div>
          <div className="text-xs text-apex-cream/60 font-semibold uppercase tracking-wider mb-2">Místo</div>
          <div className="flex gap-2 mb-3">
            <button onClick={() => set("locationType")("studio")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${form.locationType === "studio" ? "bg-apex-gold text-apex-bg" : "bg-apex-bg border border-apex-line text-apex-cream/75"}`} data-testid="admin-create-booking-location-studio">Studio</button>
            <button onClick={() => set("locationType")("mobile")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${form.locationType === "mobile" ? "bg-apex-gold text-apex-bg" : "bg-apex-bg border border-apex-line text-apex-cream/75"}`} data-testid="admin-create-booking-location-mobile">Mobilní služba</button>
          </div>
          {form.locationType === "mobile" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={form.serviceAddress} onChange={(e) => set("serviceAddress")(e.target.value)} placeholder="Adresa" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-address" />
              <input value={form.addressNote} onChange={(e) => set("addressNote")(e.target.value)} placeholder="Poznámka k adrese (volitelné)" className="px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-address-note" />
            </div>
          )}
        </div>

        <div>
          <div className="text-xs text-apex-cream/60 font-semibold uppercase tracking-wider mb-2">Platba</div>
          <div className="flex gap-2">
            <button onClick={() => set("paymentMethod")("cash")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${form.paymentMethod === "cash" ? "bg-apex-gold text-apex-bg" : "bg-apex-bg border border-apex-line text-apex-cream/75"}`} data-testid="admin-create-booking-pay-cash">Hotově / kartou na místě</button>
            <button onClick={() => set("paymentMethod")("online")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${form.paymentMethod === "online" ? "bg-apex-gold text-apex-bg" : "bg-apex-bg border border-apex-line text-apex-cream/75"}`} data-testid="admin-create-booking-pay-online">Online platba</button>
          </div>
        </div>

        <textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} placeholder="Interní poznámka (volitelné)" className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="admin-create-booking-notes" />

        <div className="flex justify-end gap-2 pt-2">
          <button disabled={busy} onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-apex-line text-apex-cream/70 hover:bg-apex-card">Zavřít</button>
          <button disabled={busy} onClick={submit} className="text-sm px-5 py-2 rounded-lg bg-apex-gold text-apex-bg font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2" data-testid="admin-create-booking-submit">
            <CalendarPlus className="w-4 h-4" /> {busy ? "Ukládám…" : "Vytvořit a potvrdit"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
